import { descriptionToHtml } from './productDescriptionHtml';

export interface WooImageItem {
  url: string;
  uploading?: boolean;
  error?: string;
}

export interface WooProductFormValue {
  name: string;
  regular_price: string;
  sale_price: string;
  sku: string;
  status: string;
  stock_status: string;
  stock_quantity: string;
  short_description: string;
  category: string;
}

export const EMPTY_WOO_PRODUCT_FORM: WooProductFormValue = {
  name: '',
  regular_price: '',
  sale_price: '',
  sku: '',
  status: 'publish',
  stock_status: 'instock',
  stock_quantity: '',
  short_description: '',
  category: '',
};

export function buildWooProductPayload(
  form: WooProductFormValue,
  images: WooImageItem[],
): Record<string, unknown> {
  const data: Record<string, unknown> = {
    name: form.name.trim(),
    regular_price: form.regular_price,
    sale_price: form.sale_price || '',
    sku: form.sku,
    status: form.status,
    stock_status: form.stock_status,
    short_description: form.short_description,
    category: form.category.trim(),
    images: images.filter((img) => !img.error).map((img) => ({ src: img.url })),
  };
  if (form.stock_quantity !== '') {
    data.stock_quantity = parseInt(form.stock_quantity, 10);
    data.manage_stock = true;
  }
  return data;
}

export type ShopifyImagePayload =
  | { attachment: string }
  | { src: string };

export interface ShopifyVariantInput {
  id?: number;
  option1: string;
  sku: string;
  price: string;
  compare_at_price?: string;
  inventory_quantity: number | '';
}

export function shopifyVariantsPayload(variants: ShopifyVariantInput[], optionName = '') {
  const multi = variants.length > 1 || variants.some((v) => v.option1.trim());
  const values = variants.map((v) => v.option1.trim()).filter(Boolean);
  return {
    ...(multi && optionName.trim()
      ? { options: [{ name: optionName.trim(), values: [...new Set(values)] }] }
      : {}),
    variants: variants.map((v) => ({
      ...(v.id != null ? { id: v.id } : {}),
      option1: v.option1.trim() || 'Default Title',
      ...(v.sku.trim() ? { sku: v.sku.trim() } : {}),
      price: String(v.price),
      ...(v.compare_at_price !== undefined
        ? { compare_at_price: v.compare_at_price.trim() !== '' ? String(v.compare_at_price) : null }
        : {}),
      inventory_quantity: v.inventory_quantity === '' ? 0 : Number(v.inventory_quantity),
    })),
  };
}

export interface ShopifyProductPayloadInput {
  title: string;
  description: string;
  vendor: string;
  productType: string;
  collectionIds?: number[];
  categoryId?: string | null;
  status: string;
  tags?: string[];
  variants: ShopifyVariantInput[];
  optionName?: string;
  images?: ShopifyImagePayload[];
}

export function buildShopifyProductPayload(
  input: ShopifyProductPayloadInput,
): Record<string, unknown> {
  const productData: Record<string, unknown> = {
    title: input.title.trim(),
    body_html: descriptionToHtml(input.description),
    vendor: input.vendor,
    product_type: input.productType,
    ...(input.collectionIds && input.collectionIds.length
      ? { collectionIds: input.collectionIds }
      : {}),
    ...(input.categoryId ? { categoryId: input.categoryId } : {}),
    status: input.status,
    ...shopifyVariantsPayload(input.variants, input.optionName),
  };
  if (input.tags && input.tags.length) {
    productData.tags = input.tags.join(', ');
  }
  if (input.images && input.images.length) {
    productData.images = input.images;
  }
  return productData;
}
